// src/components/features/chat/TriageReportCard.jsx
//
// 15 Jul 2026 — UX improvement for the download_triage_report chat tool.
// Renders a clickable "document card" in chat instead of silently opening
// the print window as a side effect of the tool call. Clicking the card (or
// its button) triggers the actual download.
//
// NOTE ON "THUMBNAIL": there is no server-generated PDF file anywhere in
// this system (see triageReportPrint.js's header comment) — "Download PDF"
// has always meant "open an HTML string in a new window and call the
// browser's native print dialog." Because of that, this card is NOT a
// pixel-accurate rendered preview of the report's first page — building one
// would require rendering the report HTML to a canvas/image client-side
// (e.g. via html2canvas), which isn't a dependency in this project today.
// Instead, this card shows real report metadata (risk level color, patient
// initials, report ID, draw date, risk score) inside a document-shaped
// visual, which conveys "here's your report" without a new dependency or
// rendering step. If a true rendered-page thumbnail is wanted later, that's
// a separate, larger addition (new library, new render pipeline).
//
// Props (all come straight from toolRouter.js's download_triage_report
// result — see that file and chatService.js's trimToolResult() case):
//   patient        { id, firstName, lastName }
//   triageResult   a single TriageResultDTO.Response (the latest)
//   triageHistory  full array, for trend context passed through to the PDF
//   reportId       e.g. "PRV-9D0EB9EC-20260707"
//   drawDate       ISO date string
//   riskLevel      e.g. "HIGH" | "MODERATE" | "LOW"
//   riskScore      0-100

import React, { useState } from 'react';
import { downloadPatientReport } from '../../../utils/patientReportDownload';
import Icon from '../../Icon';

const C = {
  navy:       'var(--brand-dark)',
  gold:       'var(--amber-text)',
  goldLight:  'var(--amber-bg)',
  goldBorder: 'var(--amber)',
  white:      'var(--bg-surface)',
  gray200:    'var(--border)',
  textPrimary:'var(--text-primary)',
  textMuted:  'var(--text-muted)',
  good:       'var(--green)',
  goodTint:   'var(--green-bg)',
  warn:       'var(--amber-text)',
  warnTint:   'var(--amber-bg)',
  bad:        'var(--red)',
  badTint:    'var(--red-bg)',
};

function riskColors(level) {
  const l = String(level || '').toUpperCase();
  if (l === 'HIGH' || l === 'CRITICAL') return { fg: C.bad,  bg: C.badTint,  label: 'HIGH' };
  if (l === 'MODERATE')                 return { fg: C.warn, bg: C.warnTint, label: 'MODERATE' };
  if (l === 'LOW')                      return { fg: C.good, bg: C.goodTint, label: 'LOW' };
  return { fg: C.textMuted, bg: C.gray200, label: l || 'UNKNOWN' };
}

function formatDrawDate(iso) {
  if (!iso) return 'Unknown date';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

export default function TriageReportCard({ patient, triageResult, triageHistory, reportId, drawDate, riskLevel, riskScore }) {
  const [status, setStatus] = useState('idle'); // idle | opening | popup_blocked | failed
  const [showOptimal, setShowOptimal] = useState(false); // optimal markers are left out of the PDF unless ticked
  const risk = riskColors(riskLevel);
  const initials = `${patient?.firstName?.[0] || ''}${patient?.lastName?.[0] || ''}`.toUpperCase() || '?';

  const handleClick = () => {
    setStatus('opening');
    const result = downloadPatientReport(patient, triageResult, { showOptimal });
    if (!result.success) {
      setStatus(result.reason === 'popup_blocked' ? 'popup_blocked' : 'failed');
      return;
    }
    setStatus('idle');
  };

  return (
    <div style={{ maxWidth: 320 }}>
      <div
        role="button"
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
        style={{
          border: `1.5px solid ${C.goldBorder}`,
          borderRadius: 12,
          overflow: 'hidden',
          cursor: 'pointer',
          background: C.white,
          boxShadow: '0 1px 3px rgba(13,30,53,0.08)',
          transition: 'box-shadow 0.15s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(13,30,53,0.14)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(13,30,53,0.08)'; }}
      >
        {/* Header bar — risk-colored, mimics a document's top accent */}
        <div style={{ background: C.navy, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon name="document" size={16} />
          <span style={{ color: C.white, fontSize: 12, fontWeight: 600, letterSpacing: 0.3 }}>
            PREVIU HEALTH REPORT
          </span>
        </div>

        {/* Body — document-style summary */}
        <div style={{ padding: '14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{
              width: 36, height: 36, borderRadius: '50%', background: C.goldLight,
              border: `1.5px solid ${C.goldBorder}`, display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 13, fontWeight: 700, color: C.gold, flexShrink: 0,
            }}>
              {initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {patient?.firstName || 'Patient'} {patient?.lastName || ''}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted }}>
                Drawn {formatDrawDate(drawDate)}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
              background: risk.bg, color: risk.fg, textTransform: 'uppercase', letterSpacing: 0.4,
            }}>
              {risk.label} RISK
            </span>
            {riskScore != null && (
              <span style={{ fontSize: 11, color: C.textMuted, fontFamily: 'monospace' }}>
                Score {riskScore}/100
              </span>
            )}
          </div>

          <div style={{ fontSize: 10, color: C.textMuted, fontFamily: 'monospace', marginBottom: 12 }}>
            {reportId}
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            padding: '8px 12px', borderRadius: 8, background: C.goldLight,
            border: `1px solid ${C.goldBorder}`, fontSize: 12, fontWeight: 600, color: C.gold,
          }}>
            {status === 'opening' ? '⏳ Opening…' : '⬇ Download PDF'}
          </div>
        </div>
      </div>

      <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.textMuted, cursor: 'pointer' }}>
        <input type="checkbox" checked={showOptimal} onChange={e => setShowOptimal(e.target.checked)} />
        Show optimal markers in the PDF
      </label>

      {status === 'popup_blocked' && (
        <div style={{ marginTop: 6, fontSize: 11, color: C.bad }}>
          ⚠ Pop-up blocked — allow pop-ups for this site in your browser, then click the card again.
        </div>
      )}
      {status === 'failed' && (
        <div style={{ marginTop: 6, fontSize: 11, color: C.bad }}>
          ⚠ Couldn't open the report — please try again.
        </div>
      )}
    </div>
  );
}
